import type { SkillInput, SkillUpdateInput } from "@/lib/queries";
import type {
  AppUser, Queue, RoutingSkill, SkillRequirement, UserSkill, UserSkills,
} from "@/lib/types";

/**
 * Skills as the console shows and edits them (ADR-015): the catalog's form, and the arithmetic
 * between a rep's skills dialog and `PUT /app-users/{id}/skills`.
 *
 * The rules are `platform`'s. The slug and the level bounds are `SLUG_RE`, `MIN_LEVEL` and
 * `MAX_LEVEL` in `services/shared/telephony/skills.py`, the default level is `DEFAULT_AGENT_LEVEL`
 * in `models/skills.py`, and the text limits are `RoutingSkillCreate` in core-api's `schemas.py`.
 * Which of a rep's skills count is what the ACD's `store.py` and core-api's `set_user_skills` both
 * check: the rep's own tenant has the skill, active, as a rep skill. The server decides all of it;
 * the copies here keep a save it would refuse off the screen and say why first. Pure, for
 * `test/skills.test.ts`.
 */

/** `SLUG_RE`: what lines, numbers, handoffs and reps name a skill by. */
export const SKILL_SLUG_RE = /^[a-z0-9][a-z0-9_-]{0,62}$/;

export const SKILL_LIMITS = { name: 80, category: 40, description: 500 } as const;

export const MIN_LEVEL = 1;
export const MAX_LEVEL = 5;
/** `DEFAULT_AGENT_LEVEL`: the middle of the scale, so a requirement at the default minimum of 1 is
 *  met and a line that rings its best rep first can still rank this one. */
export const DEFAULT_LEVEL = 3;
export const LEVELS: readonly number[] = Array.from(
  { length: MAX_LEVEL - MIN_LEVEL + 1 },
  (_, i) => MIN_LEVEL + i,
);

/** A level the server takes: whole, from 1 to 5. Anything else becomes the default, never a 422. */
export function clampLevel(level: number): number {
  if (!Number.isFinite(level)) return DEFAULT_LEVEL;
  return Math.min(MAX_LEVEL, Math.max(MIN_LEVEL, Math.round(level)));
}

// --- the catalog -------------------------------------------------------------------------------

/** The catalog's form. A category or description left empty is `""`. */
export interface SkillValues {
  slug: string;
  name: string;
  category: string;
  description: string;
  is_agent_skill: boolean;
  is_lead_tag: boolean;
}

/** `RoutingSkillCreate`'s defaults: a rep skill, not a lead tag. */
export const NEW_SKILL: SkillValues = {
  slug: "",
  name: "",
  category: "",
  description: "",
  is_agent_skill: true,
  is_lead_tag: false,
};

export function skillValues(skill: RoutingSkill): SkillValues {
  return {
    slug: skill.slug,
    name: skill.name,
    category: skill.category ?? "",
    description: skill.description ?? "",
    is_agent_skill: skill.is_agent_skill,
    is_lead_tag: skill.is_lead_tag,
  };
}

/** What `POST /skills` takes: trimmed, with an empty category or description sent as null. */
export function newSkillPayload(values: SkillValues): Required<Omit<SkillInput, "tenant_id">> {
  return {
    slug: values.slug.trim(),
    name: values.name.trim(),
    category: values.category.trim() || null,
    description: values.description.trim() || null,
    is_agent_skill: values.is_agent_skill,
    is_lead_tag: values.is_lead_tag,
  };
}

/**
 * The fields an edit changed, as `PUT /skills/{id}` takes them, and nothing else. The form holds no
 * `active`, so saving a name never restores a skill somebody retired while the form was open. The
 * slug is never among them: lines, numbers, handoffs and reps all name the skill by it.
 */
export function skillChanges(values: SkillValues, skill: RoutingSkill): SkillUpdateInput {
  const next = newSkillPayload(values);
  const out: SkillUpdateInput = {};
  if (next.name !== skill.name) out.name = next.name;
  if (next.category !== (skill.category || null)) out.category = next.category;
  if (next.description !== (skill.description || null)) out.description = next.description;
  if (next.is_agent_skill !== skill.is_agent_skill) out.is_agent_skill = next.is_agent_skill;
  if (next.is_lead_tag !== skill.is_lead_tag) out.is_lead_tag = next.is_lead_tag;
  return out;
}

/** The categories in use, each once, for the form to suggest: a tenant groups by whatever it likes,
 *  and `Language` typed a second way is a second group. */
export function skillCategories(skills: readonly Pick<RoutingSkill, "category">[]): string[] {
  const seen = new Map<string, string>();
  for (const { category } of skills) {
    const name = category?.trim();
    if (name && !seen.has(name.toLowerCase())) seen.set(name.toLowerCase(), name);
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b));
}

/** The catalog as the page lists it: the server's order (category, then name), retired skills last. */
export function catalogOrder<T extends Pick<RoutingSkill, "active">>(skills: readonly T[]): T[] {
  return [...skills].sort((a, b) => Number(b.active) - Number(a.active));
}

// --- a rep's skills ----------------------------------------------------------------------------

/** What a rep can be given: their own tenant's active rep skills, in catalog order. A superadmin
 *  reads every tenant's catalog, and a rep's set is checked against their own tenant's. */
export function assignableSkills(
  catalog: readonly RoutingSkill[],
  tenantId: number | null,
): RoutingSkill[] {
  return catalog.filter((s) => s.tenant_id === tenantId && s.active && s.is_agent_skill);
}

export interface HeldSkill {
  slug: string;
  level: number;
  /** The rep's tenant's catalog entry, or null when it has none by that slug. */
  skill: RoutingSkill | null;
  /** Why the ACD does not count it, and a save cannot send it back; null for a skill that counts. */
  problem: string | null;
}

/** What a rep holds, in slug order, each with its catalog entry and — for one that no longer
 *  counts — the reason. */
export function heldSkills(
  held: readonly UserSkill[],
  catalog: readonly RoutingSkill[],
  tenantId: number | null,
): HeldSkill[] {
  const own = new Map(catalog.filter((s) => s.tenant_id === tenantId).map((s) => [s.slug, s]));
  return [...held].sort(bySlug).map(({ slug, level }) => {
    const skill = own.get(slug) ?? null;
    const problem = !skill
      ? "Not in the catalog"
      : !skill.active
        ? "Retired"
        : !skill.is_agent_skill
          ? "Not a rep skill"
          : null;
    return { slug, level, skill, problem };
  });
}

/** A rep's skills as the dialog starts them: each held skill that can be sent back, at its level. */
export function initialSelection(
  held: readonly UserSkill[],
  catalog: readonly RoutingSkill[],
  tenantId: number | null,
): Record<string, number> {
  return Object.fromEntries(
    heldSkills(held, catalog, tenantId)
      .filter((h) => h.problem === null)
      .map((h) => [h.slug, clampLevel(h.level)]),
  );
}

/**
 * The body of `PUT /app-users/{id}/skills`. The server REPLACES the rep's set with it, so it is the
 * whole set, never a change: every ticked skill the tenant can give, each once, at a level the
 * server takes, in slug order.
 */
export function userSkillsPayload(
  selection: Readonly<Record<string, number>>,
  assignable: readonly Pick<RoutingSkill, "slug">[],
): { skills: UserSkill[] } {
  const allowed = new Set(assignable.map((s) => s.slug));
  return {
    skills: Object.entries(selection)
      .filter(([slug]) => allowed.has(slug))
      .map(([slug, level]) => ({ slug, level: clampLevel(level) }))
      .sort(bySlug),
  };
}

/** Whether saving changes what the rep holds. Losing a skill that cannot be sent back counts. */
export function skillSetChanged(
  selection: Readonly<Record<string, number>>,
  held: readonly UserSkill[],
  assignable: readonly Pick<RoutingSkill, "slug">[],
): boolean {
  const next = userSkillsPayload(selection, assignable).skills;
  const before = [...held].sort(bySlug);
  return (
    next.length !== before.length ||
    next.some((s, i) => s.slug !== before[i]?.slug || s.level !== before[i]?.level)
  );
}

/** Why an account's skills cannot be set, or null when they can: the two refusals core-api's
 *  `set_user_skills` answers with one 422, told apart. */
export function skillsBlockedReason(
  account: Pick<AppUser, "tenant_id">,
  skills: Pick<UserSkills, "user">,
): string | null {
  if (skills.user === null) {
    return "Skills belong to the phone the ACD rings, and this account has no phone.";
  }
  if (account.tenant_id === null) {
    return "Skills come from a tenant's catalog, and this account belongs to no tenant.";
  }
  return null;
}

// --- a number's routing tags -------------------------------------------------------------------
//
// A number can require skills of every call to it: a Spanish line is a number tagged `spanish`
// (ADR-015). The gateway renders the tags as `acl_required_skills`, and the ACD adds them to the
// line's own requirements. core-api's `routers/dids.py` checks each slug with the same
// `skill_problem` a rep's skills get: the number's tenant has it, active, as a rep skill.

/** `ACD_ROUTES` in core-api: the routes that reach the ACD, and so the only ones tags apply to. */
export const TAGGED_ROUTES: readonly string[] = ["campaign", "queue"];

export function routeTakesTags(routeType: string | null): boolean {
  return routeType !== null && TAGGED_ROUTES.includes(routeType);
}

/** A new tag asks for level 1, "holds the skill at all", so every rep who holds it qualifies. */
export const DEFAULT_TAG_LEVEL = MIN_LEVEL;

const asHeld = (tags: readonly SkillRequirement[]): UserSkill[] =>
  tags.map((t) => ({ slug: t.slug, level: t.min_level }));

/** A number's tags as stored, each with its catalog entry and, for one the server would now refuse,
 *  the reason. `level` is the tag's minimum. */
export function numberTags(
  tags: readonly SkillRequirement[],
  catalog: readonly RoutingSkill[],
  tenantId: number | null,
): HeldSkill[] {
  return heldSkills(asHeld(tags), catalog, tenantId);
}

/** The tags as the route dialog starts them: each one the server still takes, at its minimum. */
export function initialTags(
  tags: readonly SkillRequirement[],
  catalog: readonly RoutingSkill[],
  tenantId: number | null,
): Record<string, number> {
  return initialSelection(asHeld(tags), catalog, tenantId);
}

/** `routing_tags` for `PUT /dids/{id}`: the whole set, each tag the tenant can require, once, in
 *  slug order. The server replaces the number's tags with it. */
export function routingTagsPayload(
  selection: Readonly<Record<string, number>>,
  assignable: readonly Pick<RoutingSkill, "slug">[],
): SkillRequirement[] {
  return userSkillsPayload(selection, assignable).skills.map(({ slug, level }) => ({
    slug,
    min_level: level,
  }));
}

/** Ticked skills no rep holds. A call needing one waits for a rep who never comes. */
export function unheldTags(
  selection: Readonly<Record<string, number>>,
  assignable: readonly RoutingSkill[],
): RoutingSkill[] {
  return assignable.filter((s) => selection[s.slug] !== undefined && s.holders === 0);
}

/** `spanish`, `closer 2+, spanish`: a requirement set in one line, level 1 left unsaid. */
export function formatRequirements(reqs: readonly SkillRequirement[]): string {
  return [...reqs]
    .sort(bySlug)
    .map((r) => (r.min_level > MIN_LEVEL ? `${r.slug} ${r.min_level}+` : r.slug))
    .join(", ");
}

/**
 * What happens to a caller on `line` while no rep with the number's skills is free, in the ACD's
 * terms (`matching.py`): a line with `skill_relax_seconds` lifts every requirement after that long
 * (the line's own too), and otherwise the caller waits until `max_wait_seconds` hangs them up.
 */
export function taggedCallerWait(
  line: Pick<Queue, "name" | "max_wait_seconds" | "skill_relax_seconds" | "required_skills">,
): string {
  const own = line.required_skills.length
    ? ` The line already needs ${formatRequirements(line.required_skills)}, so a rep needs both.`
    : "";
  if (line.skill_relax_seconds > 0) {
    return `If no such rep takes the call within ${seconds(line.skill_relax_seconds)}, line ${line.name} drops its skill requirements and any rep on it may answer.${own}`;
  }
  if (line.max_wait_seconds > 0) {
    return `Line ${line.name} never drops the requirement: a caller waits up to ${seconds(line.max_wait_seconds)} for such a rep, then is hung up.${own}`;
  }
  return `Line ${line.name} never drops the requirement and has no max wait: a caller waits for such a rep as long as it takes.${own}`;
}

function seconds(total: number): string {
  const s = Math.round(total);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  return s % 60 ? `${m} min ${s % 60} s` : `${m} min`;
}

function bySlug(a: { slug: string }, b: { slug: string }): number {
  return a.slug < b.slug ? -1 : a.slug > b.slug ? 1 : 0;
}
